import React from 'react';
import {
    FormGroup,
    Button,
    List,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../../Common';
import {
    FormLabel,
    toasty,
    FormCheckbox,
    FlexColumnStart,
} from '../../common/forms/FormElements';
import { SaveButton } from '../../common/forms/SaveButton';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { ModalForm } from '../../common/forms/ModalForm';
import { MoveContractCompatibilityAlert } from '../../common/MoveContractCompatibilityAlert';

// Matches ContractProjectsFeatureFlagTypes.AllContracts — every contract on the tenant uses projects.
const USE_CONTRACT_PROJECTS_ALL_CONTRACTS = 2;
const CreateNewProjectID = -1;

const formatProjectOption = (option, { context }) => {
    if (context === 'menu') {
        return (
            <FlexColumnStart>
                <span>{option.projectNumber}</span>
                {Boolean(option.workOrderNumber) && (
                    <small className="text-muted mt-1">
                        WO#: {option.workOrderNumber}
                    </small>
                )}
            </FlexColumnStart>
        );
    }

    return <span>{option.projectNumber}</span>;
};

class MoveTimesheetToContractVM {
    timesheetIds = [];

    contractId = 0;

    surcharges = [];

    subcontractor = 0;

    moveEntireJob = false;

    allowProcessedPayrollMove = false;

    destinationProjectId = null;

    createNewProject = false;
}

/**
 * Shared class component for moving timesheets to a different contract.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */
class MoveTimesheetToContractForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            moveTimesheet: new MoveTimesheetToContractVM(),
            selectedTimesheetOption: [],
            selectedContractOption: null,
            selectedSubcontractorOption: null,
            applyingCharges: [],
            timesheetCharges: [],
            surchargeModalOpen: false,
            basedOnChargeTypeOption: null,
            appliedChargeTypeOption: null,
            replaceCharges: false,
            selectedProjectOption: null,
            destinationContract: null,
            loadingDestinationContract: false,
            contractMoveBlocked: false,
        };
    }

    onContractMoveBlockedChange = (contractMoveBlocked) => this.setState({ contractMoveBlocked });

    toggleReplaceCharges = ({ target }) => this.setState({ replaceCharges: target.checked });

    toggleMoveEntireJob = ({ target }) => {
        this.setState((prev) => ({
            moveTimesheet: {
                ...prev.moveTimesheet,
                moveEntireJob: target.checked,
            },
        }));
    };

    toggleAllowProcessedPayrollMove = ({ target }) => {
        this.setState((prev) => ({
            moveTimesheet: {
                ...prev.moveTimesheet,
                allowProcessedPayrollMove: target.checked,
            },
        }));
    };

    toggleSurchargeModal = () => {
        this.setState((prev) => ({
            surchargeModalOpen: !prev.surchargeModalOpen,
            ...(prev.surchargeModalOpen && {
                basedOnChargeTypeOption: null,
                appliedChargeTypeOption: null,
                applyingCharges: [],
                replaceCharges: false,
            }),
        }));
    };

    onBasedOnChargeTypeChanged = (option) => {
        const { moveTimesheet } = this.state;

        this.setState(
            {
                basedOnChargeTypeOption: option,
                appliedChargeTypeOption: null,
                applyingCharges: [],
            },
            async () => {
                if (option && moveTimesheet.contractId) {
                    await this.loadApplyChargeOptions(option.value, moveTimesheet.contractId);
                }
            },
        );
    };

    onAppliedChargeTypeChanged = (option) => {
        this.setState({ appliedChargeTypeOption: option });
    };

    addSurcharge = () => {
        const {
            basedOnChargeTypeOption,
            appliedChargeTypeOption,
            replaceCharges,
        } = this.state;
        if (!basedOnChargeTypeOption || !appliedChargeTypeOption) return;

        const pair = {
            basedOnChargeTypeId: Number(basedOnChargeTypeOption.value),
            basedOnChargeTypeLabel: basedOnChargeTypeOption.label,
            appliedChargeTypeId: Number(appliedChargeTypeOption.value),
            appliedChargeTypeLabel: appliedChargeTypeOption.label,
            replaceCharge: replaceCharges,
        };

        this.setState((prev) => ({
            moveTimesheet: {
                ...prev.moveTimesheet,
                surcharges: [...prev.moveTimesheet.surcharges, pair],
            },
            surchargeModalOpen: false,
            basedOnChargeTypeOption: null,
            appliedChargeTypeOption: null,
        }));
    };

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { moveTimesheet } = { ...this.state };
        moveTimesheet.userId = this.context.user.employeeId;

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.moveTimesheetToContract(), moveTimesheet);

            this.setState({
                moveTimesheet: new MoveTimesheetToContractVM(),
                selectedTimesheetOption: [],
                selectedContractOption: null,
                selectedSubcontractorOption: null,
                timesheetCharges: [],
                selectedProjectOption: null,
                destinationContract: null,
            });

            toasty.success('Move Timesheet Succeeded');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error(err);
            toasty.error(err?.handledError?.message ?? 'Error: Move Timesheet Failed. Please check Timesheet and Contract Number');
            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    loadTimesheetOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.timesheetNumberSearch(inputValue))
            .then((data) => callback(data));
    }, 250);

    onTimesheetChanged = async (selectedOptions) => {
        const timesheetIds = selectedOptions ? selectedOptions.map((option) => Number(option.value)) : [];
        this.setState((prevState) => ({
            moveTimesheet: {
                ...prevState.moveTimesheet,
                timesheetIds,
            },
            selectedTimesheetOption: selectedOptions,
        }));

        if (timesheetIds.length) {
            await this.loadTimesheetCharges(timesheetIds[0]);
        } else {
            this.setState({ timesheetCharges: [] });
        }
    };

    loadContractOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.contractsSearch(inputValue))
            .then((data) => callback(data));
    }, 250);

    onContractChanged = async (selectedOption) => {
        const contractId = selectedOption ? Number(selectedOption.value) : 0;
        this.setState((prevState) => ({
            moveTimesheet: {
                ...prevState.moveTimesheet,
                contractId,
                destinationProjectId: null,
                createNewProject: false,
            },
            selectedContractOption: selectedOption,
            selectedProjectOption: null,
            destinationContract: null,
            loadingDestinationContract: Boolean(contractId),
        }));

        const { basedOnChargeTypeOption } = this.state;

        if (basedOnChargeTypeOption && contractId) {
            await this.loadApplyChargeOptions(basedOnChargeTypeOption.value, contractId);
        }


        if (!contractId) return;

        // The projects the destination offers come from the contract itself, same as Quick Job.
        try {
            const destinationContract = await httpClient.js(ApiRoutes.contract.byId(contractId));
            this.setState({ destinationContract, loadingDestinationContract: false });
        } catch (err) {
            console.error('Unable to load the destination contract.', err);
            this.setState({ loadingDestinationContract: false });
        }
    };

    onProjectChanged = (selectedOption) => {
        const createNewProject = selectedOption?.id === CreateNewProjectID;

        this.setState((prevState) => ({
            moveTimesheet: {
                ...prevState.moveTimesheet,
                createNewProject,
                destinationProjectId: createNewProject ? null : (selectedOption?.id ?? null),
            },
            selectedProjectOption: selectedOption,
        }));
    };

    loadApplyChargeOptions = async (baseChargeTypeId, contractId) => {
        if (!contractId) return;
        try {
            const data = await httpClient.js(
                ApiRoutes.timesheet.getSameTypeCharges(contractId, baseChargeTypeId),
            );

            const options = data.map(({ id, description }) => ({
                value: id,
                label: description,
            }));

            this.setState({ applyingCharges: options });
        } catch (err) {
            console.error('Error fetching applying charge types:', err);
            this.setState({ applyingCharges: [] });
        }
    };

    loadTimesheetCharges = async (timesheetId) => {
        try {
            const data = await httpClient.js(ApiRoutes.timesheet.getTimesheetCharges(timesheetId));

            const options = data.map(({ id, description }) => ({
                value: id,
                label: description,
            }));

            this.setState({ timesheetCharges: options });
        } catch (err) {
            console.error('Error fetching timesheet charge types:', err);
            this.setState({ timesheetCharges: [] });
        }
    };

    loadSubcontractorOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.subcontractorSearch(inputValue))
            .then((data) => callback(data.map((item) => ({
                label: `${item.label} - [${item.active}]`,
                value: item.value,
            }))));
    }, 250);

    onSubcontractorChanged = (selectedOption) => {
        const companyId = selectedOption ? Number(selectedOption.value) : 0;
        this.setState((prevState) => ({
            moveTimesheet: {
                ...prevState.moveTimesheet,
                subcontractor: companyId,
            },
            selectedSubcontractorOption: selectedOption,
        }));
    };

    removeSurcharge = (idx) => {
        this.setState((prev) => ({
            moveTimesheet: {
                ...prev.moveTimesheet,
                surcharges: prev.moveTimesheet.surcharges.filter((_, i) => i !== idx),
            },
        }));
    };

    render() {
        const {
            saving,
            selectedTimesheetOption,
            selectedContractOption,
            selectedSubcontractorOption,
            moveTimesheet,
            surchargeModalOpen,
            timesheetCharges,
            applyingCharges,
            basedOnChargeTypeOption,
            appliedChargeTypeOption,
            replaceCharges,
            selectedProjectOption,
            destinationContract,
            loadingDestinationContract,
            contractMoveBlocked,
        } = this.state;

        const { moveEntireJob, allowProcessedPayrollMove } = moveTimesheet;
        const { isAdminToolPath } = this.props;

        if (!(this.context ?? {}).user) return null;

        const allContractsUseProjects = this.context.tenant?.tenantSettings?.useContractProjects === USE_CONTRACT_PROJECTS_ALL_CONTRACTS;

        const destinationUsesProjects = Boolean(destinationContract) && (allContractsUseProjects || Boolean(destinationContract.isProjectContract));
        const shouldRenderProjectSelect = Boolean(selectedContractOption) && (allContractsUseProjects || loadingDestinationContract || destinationUsesProjects);
        const projectSelectionMissing = destinationUsesProjects && !selectedProjectOption;

        const destinationProjectOptions = destinationUsesProjects ? [
            { id: CreateNewProjectID, projectNumber: '+ Create New Project' },
            ...(destinationContract?.projects || [])
                .filter((project) => !project.isClosed)
                .map((project) => ({
                    ...project,
                    projectNumber: `${destinationContract.number}-${project.contractSequence}`,
                })),
        ] : [];

        return (
            <>
                <form onSubmit={this.onSubmit}>
                    <ValidatedSelect
                        component={AsyncSelect}
                        loadOptions={this.loadTimesheetOptions}
                        placeholder="Type Timesheet Numbers Here"
                        id="timesheetNumber"
                        label="Timesheet Numbers"
                        value={selectedTimesheetOption}
                        onChange={this.onTimesheetChanged}
                        isMulti
                        required
                        error="At least one Timesheet Number is required."
                    />

                    <ValidatedSelect
                        component={AsyncSelect}
                        loadOptions={this.loadContractOptions}
                        placeholder="Type Contract Number Here"
                        id="contractNum"
                        label="Contract Number"
                        value={selectedContractOption}
                        onChange={this.onContractChanged}
                        cacheOptions
                        required
                        error="Contract Number is required."
                    />

                    {shouldRenderProjectSelect && (
                        <ValidatedSelect
                            id="destinationProject"
                            name="destinationProject"
                            options={destinationProjectOptions}
                            getOptionLabel={(option) => option.projectNumber}
                            getOptionValue={(option) => option.id}
                            label="Project"
                            valueField="id"
                            formatOptionLabel={formatProjectOption}
                            value={selectedProjectOption}
                            onChange={this.onProjectChanged}
                            isLoading={loadingDestinationContract}
                            placeholder={loadingDestinationContract ? 'Loading projects...' : 'Select project'}
                            isDisabled={loadingDestinationContract}
                            required
                            error="A project selection is required."
                        />
                    )}

                    <ValidatedSelect
                        component={AsyncSelect}
                        loadOptions={this.loadSubcontractorOptions}
                        placeholder="Enter subcontractor name"
                        id="subcontractorCompanyId"
                        label="Subcontractor (Optional)"
                        value={selectedSubcontractorOption}
                        onChange={this.onSubcontractorChanged}
                    />

                    <MoveContractCompatibilityAlert
                        url={ApiRoutes.adminTool.validateMoveTimesheetToContract()}
                        model={{
                            timesheetIds: moveTimesheet.timesheetIds,
                            contractId: moveTimesheet.contractId,
                            surcharges: moveTimesheet.surcharges,
                            moveEntireJob: moveTimesheet.moveEntireJob,
                        }}
                        onBlockedChange={this.onContractMoveBlockedChange}
                    />

                    {/* Charge Modifications Section */}
                    <div className="mt-4 mb-3">
                        <h6 className="font-weight-bold">Charge Modifications</h6>

                        <div className="d-flex mb-2">
                            <Button
                                color="secondary"
                                onClick={this.toggleSurchargeModal}
                                disabled={!selectedTimesheetOption || selectedTimesheetOption.length === 0}
                            >
                                Add or Replace Charge
                            </Button>
                        </div>

                        {moveTimesheet.surcharges.length > 0 ? (
                            <List className="list-group mb-3">
                                {moveTimesheet.surcharges.map((s, idx) => (
                                    <li
                                        key={idx}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                    >
                                        <span>
                                            <strong>{s.appliedChargeTypeLabel}</strong>
                                            {' to every '}
                                            <strong>{s.basedOnChargeTypeLabel}</strong>
                                            {s.replaceCharge && <span className="text-muted ml-2">(replacing)</span>}
                                        </span>

                                        <Button
                                            close
                                            aria-label="Remove"
                                            onClick={() => this.removeSurcharge(idx)}
                                        />
                                    </li>
                                ))}
                            </List>
                        ) : (
                            <p className="text-muted small mb-0">
                                No charge modifications configured. The timesheet will be moved as-is.
                            </p>
                        )}
                    </div>

                    <FormGroup className="mt-3">
                        <FormCheckbox
                            id="moveEntireJob"
                            name="moveEntireJob"
                            checked={moveEntireJob}
                            onChange={this.toggleMoveEntireJob}
                            labelText="Move the entire job to the new contract even if I haven't entered all timesheets"
                        />
                    </FormGroup>

                    {isAdminToolPath && (
                        <FormGroup className="mt-3">
                            <FormCheckbox
                                id="allowProcessedPayrollMove"
                                name="allowProcessedPayrollMove"
                                checked={allowProcessedPayrollMove}
                                onChange={this.toggleAllowProcessedPayrollMove}
                                labelText="Allow move when selected timesheets have processed payroll"
                            />
                        </FormGroup>
                    )}

                    <div className="d-flex justify-content-center mt-4">
                        <SaveButton
                            saving={saving}
                            disabled={
                                !selectedTimesheetOption
                                || selectedTimesheetOption.length === 0
                                || !selectedContractOption
                                || loadingDestinationContract
                                || projectSelectionMissing
                                || contractMoveBlocked
                            }
                            label="Move Timesheet"
                        />
                    </div>
                </form>

                <ModalForm
                    isOpen={surchargeModalOpen}
                    onSubmit={() => this.addSurcharge()}
                    toggle={() => this.toggleSurchargeModal()}
                    title="Add or Replace a Charge"
                    SaveButtonProps={{
                        label: 'Add',
                        disabled: !basedOnChargeTypeOption || !appliedChargeTypeOption,
                    }}
                >
                    <FormGroup>
                        <FormLabel
                            text="Charges are based on"
                            htmlFor="basedOnChargeType"
                            required
                        />
                        <ValidatedSelect
                            id="basedOnChargeType"
                            name="basedOnChargeType"
                            options={timesheetCharges}
                            value={basedOnChargeTypeOption}
                            onChange={this.onBasedOnChargeTypeChanged}
                            placeholder="Select a charge type"
                            error="You must select a charge type"
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel
                            text="Charge Type to add"
                            htmlFor="appliedChargeType"
                            required
                        />
                        <ValidatedSelect
                            id="appliedChargeType"
                            name="appliedChargeType"
                            options={applyingCharges}
                            value={appliedChargeTypeOption}
                            onChange={this.onAppliedChargeTypeChanged}
                            placeholder="Select a charge type first"
                            isDisabled={!applyingCharges.length}
                            error="You must select a charge type"
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormCheckbox
                            id="replaceCharges"
                            name="replaceCharges"
                            checked={replaceCharges}
                            onChange={this.toggleReplaceCharges}
                            labelText="Replace Charges"
                        />
                    </FormGroup>
                </ModalForm>
            </>
        );
    }
}

/**
 * Renders the execution history details for MoveTimesheetToContract.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const MoveTimesheetToContractHistoryDetails = ({ record }) => (
    <>
        <div>
            <strong>From:</strong>
            <List type="unstyled">
                {record.input.input_timesheet_number && (
                    <li>
                        <strong>Timesheets:</strong>
                        {' '}
                        {record.input.input_timesheet_number.join(', ')}
                    </li>
                )}
                {record.input.to_contract_number && (
                    <li>
                        <strong>To Contract:</strong>
                        {' '}
                        {record.input.to_contract_number}
                    </li>
                )}
                {record.input.to_project_number && (
                    <li>
                        <strong>To Project:</strong>
                        {' '}
                        {record.input.to_project_number}
                    </li>
                )}
                {record.input.surcharges && record.input.surcharges.length > 0 && (
                    <li>
                        <strong>Charges Modified:</strong>
                        {' '}
                        {record.input.surcharges.join(', ')}
                    </li>
                )}
                {record.input.subcontractor && (
                    <li>
                        <strong>Subcontractor:</strong>
                        {' '}
                        {record.input.subcontractor}
                    </li>
                )}
            </List>
        </div>

        <div>
            <strong>To:</strong>
            <List type="unstyled">
                {record.output.moved_timesheet && (
                    <li>
                        <strong>Timesheet Moved:</strong>
                        {' '}
                        {record.output.moved_timesheet.join(', ')}
                    </li>
                )}
                {Array.isArray(record.output.failed_Timesheet) && record.output.failed_Timesheet.length > 0 && (
                    <li>
                        <strong>Timesheet Move Failed:</strong>
                        {' '}
                        {record.output.failed_Timesheet.join(', ')}
                    </li>
                )}
                {record.output.added_charge && record.output.added_charge.length > 0 ? (
                    <li>
                        <strong>Charges Added/Replaced:</strong>
                        {' '}
                        {record.output.added_charge.join(', ')}
                    </li>
                ) : (
                    <li>
                        <strong>Charges:</strong>
                        {' No Charges Added/Replaced'}
                    </li>
                )}
                {record.output.subcontractor && (
                    <li>
                        <strong>Subcontractor Added:</strong>
                        {' '}
                        {record.output.subcontractor}
                    </li>
                )}
            </List>
        </div>
    </>
);

export { MoveTimesheetToContractForm };
export default MoveTimesheetToContractForm;
